import React, { useEffect, useRef, useState, useMemo } from 'react';
import { useIntersection } from 'react-use';
import PropTypes from 'prop-types';
import JBImage from 'components/essentials/JBImage';
import CtaButton from 'components/essentials/Cta/CtaButton';
import SingleCollectionCards from 'components/consumer/SingleCollectionCards/SingleCollectionCards';
import SuspenseWithLoader from 'components/consumer/SuspenseWithLoader';
import FixedWidthContainer from 'components/consumer/FixedWidthContainer';
import Grid4 from 'components/icons/Grid4';
import { colorWhite, colorGrayLight8 } from 'styles/layout/colors';
import isExternalLink from 'commons/isExternalLink';
import getSofaBuilderLink from 'commons/getSofaBuilderLink';
import useScreen from 'hooks/useScreen';
import ModularSofasCollectionPlaceholder from './ModularSofasCollectionPlaceholder';
import ModularProductCard from './ModularProductCard';

const ModularSofasCollection = React.forwardRef(
  (
    {
      collection = {},
      onCollectionInView,
      className = '',
      collectionIndex,
      isFirstCollection = false,
    },
    ref
  ) => {
    const intersectionRef = useRef(null);
    const [hasLoaded, setHasLoaded] = useState(isFirstCollection);

    const intersection = useIntersection(intersectionRef, {
      root: null,
      rootMargin: '0px',
      threshold: 0,
    });

    const { isMobile } = useScreen();

    const {
      products = [],
      name = '',
      id,
      description = '',
      image = '',
      productLink = '',
      link = '',
    } = collection;

    const bundleProductIds = products.map(p => p.id);
    const sortedProducts = useMemo(
      () =>
        [...products].sort((a, b) => {
          if (
            a.name.toLowerCase().includes('slipcovered') &&
            !b.name.toLowerCase().includes('slipcovered')
          ) {
            return -1;
          }
          if (
            !a.name.toLowerCase().includes('slipcover') &&
            b.name.toLowerCase().includes('slipcover')
          ) {
            return 1;
          }
          return 0;
        }),
      [products]
    );

    useEffect(() => {
      if (intersection?.isIntersecting) {
        if (!hasLoaded) {
          setHasLoaded(true);
        }
        onCollectionInView?.(id);
      }
    }, [intersection?.isIntersecting, hasLoaded, id, onCollectionInView]);

    const onBuildClicked = () => {
      const sofaBuilderLink = getSofaBuilderLink(
        name,
        `/modular/${productLink}/`
      );

      if (isExternalLink(sofaBuilderLink)) {
        window.open(sofaBuilderLink);
      } else {
        // force reload modular page to handle marxent performance issues
        // https://stitchindustries.atlassian.net/browse/CON-6483
        window.location.href = sofaBuilderLink;
      }
    };

    const sofasCollection = (
      <div>
        <FixedWidthContainer className="!w-full flex flex-col md:flex-row max-[1200px]:md:justify-between">
          <div className="items-start flex flex-col md:flex-[0.5] md:px-2 md:py-20">
            <h2 className="text-2xl font-bold text-gray mb-0 md:text-[clamp(1.75rem,5.2vw,2.5rem)] md:leading-[clamp(2.25rem,6.25vw,3rem)] md:tracking-[.0125rem] md:font-bold">
              {name}
            </h2>
            <div className="text-sm mb-6 mt-2 md:block">{description}</div>
            {isMobile && (
              <div className="items-center flex justify-center max-[767px]:pt-5 md:flex-1 md:p-[1.7rem] md:max-w-[calc(100vw_-_350px)] max-[1200px]:md:max-w-[calc(100vw_-_500px)] [&>img]:max-[767px]:max-h-full [&>img]:max-[767px]:relative [&>img]:max-[767px]:top-[20%] [&>img]:max-[767px]:w-auto">
                <JBImage
                  sizes="70w"
                  src={image}
                  fm="auto"
                  bg={
                    (collectionIndex + 1) % 2 === 0
                      ? colorGrayLight8
                      : colorWhite
                  }
                />
              </div>
            )}
            <div className="flex flex-row flex-nowrap mb-4 w-full max-[767px]:overflow-x-scroll max-[767px]:mt-4 max-[767px]:mx-0 max-[767px]:mb-2">
              <div className="max-[767px]:flex max-[767px]:flex-row max-[767px]:flex-nowrap max-[767px]:w-screen max-[767px]:overflow-x-scroll [&::-webkit-scrollbar]:max-[767px]:appearance-none [&::-webkit-scrollbar]:max-[767px]:w-0 [&::-webkit-scrollbar]:max-[767px]:h-0 [&::-webkit-scrollbar]:max-[767px]:hidden md:grid md:gap-[10px] md:grid-cols-[repeat(auto-fit,minmax(130px,25%)_)] md:w-full">
                {sortedProducts.map(product => (
                  <div
                    key={`${product.id}-${product.slug}`}
                    className="max-[767px]:!flex-shrink-0 max-[767px]:!mr-[.3rem] max-[767px]:!max-w-[110px] max-[767px]:!w-[110px] max-[767px]:!flex [&>span]:max-[767px]:!flex [&>span]:max-[767px]:!w-full max-[767px]:bg-gray-light8"
                  >
                    <ModularProductCard
                      product={product}
                      overrideProductState={collection}
                      displayType="product"
                      overrideLink={`/modular/${productLink}/`}
                    />
                  </div>
                ))}
              </div>
            </div>
            <CtaButton
              className="w-full mb-4 md:w-auto md:mb-0 md:mt-4"
              styling="solid-button"
              color="brand"
              block
              onClick={onBuildClicked}
            >
              <Grid4 width={20} height={20} fill={colorWhite} /> Build your own{' '}
              {name}
            </CtaButton>
          </div>

          {!isMobile && (
            <div className="items-center flex justify-center max-[767px]:pt-5 md:flex-1 md:p-[1.7rem] md:max-w-[calc(100vw_-_350px)] max-[1200px]:md:max-w-[calc(100vw_-_500px)] [&>img]:max-[767px]:max-h-full [&>img]:max-[767px]:relative [&>img]:max-[767px]:top-[20%] [&>img]:max-[767px]:w-auto">
              <JBImage
                width={1000}
                src={image}
                fm="auto"
                bg={
                  (collectionIndex + 1) % 2 === 0 ? colorGrayLight8 : colorWhite
                }
              />
            </div>
          )}
        </FixedWidthContainer>
        <FixedWidthContainer className="!w-full flex flex-col md:flex-row max-[1200px]:md:justify-between">
          <div className="pb-20 w-full">
            <h3 className="text-xl font-bold m-0 px-2 py-3 text-left max-[767px]:text-lg max-[767px]:font-bold max-[767px]:mt-10 max-[767px]:p-0">{`Pre-configured ${name} options`}</h3>
            {hasLoaded && (
              <SuspenseWithLoader
                fallback={<ModularSofasCollectionPlaceholder />}
              >
                <SingleCollectionCards
                  slug={link}
                  skipProductIds={bundleProductIds}
                  modifier="modular"
                />
              </SuspenseWithLoader>
            )}
          </div>
        </FixedWidthContainer>
      </div>
    );

    return (
      <div ref={intersectionRef}>
        <div
          className={`flex overflow-hidden relative no-underline flex-col px-0 py-6 md:h-auto md:p-0 [&_>_*]:flex-1 ${className}`}
          ref={ref}
        >
          {sofasCollection}
        </div>
      </div>
    );
  }
);

ModularSofasCollection.propTypes = {
  collection: PropTypes.shape({
    id: PropTypes.number,
    path: PropTypes.string,
  }),
  onCollectionInView: PropTypes.func,
  className: PropTypes.string,
  collectionIndex: PropTypes.number,
  isFirstCollection: PropTypes.bool,
};

export default React.memo(ModularSofasCollection);
